export const MCP_GITHUB_CONNECTOR_ID_KEY = "xhandle.mcp.githubConnectorId";
export const MCP_GITHUB_CONNECTOR_CHANGED_EVENT = "xhandle:mcp-github-connector-changed";
export const MCP_GITHUB_REPOSITORIES_KEY = "xhandle.mcp.githubRepositories";
export const MCP_GITHUB_REPOSITORY_CHANGED_EVENT = "xhandle:mcp-github-repository-changed";
export const MCP_GITHUB_USER_CONNECTOR_KEY = "xhandle.mcp.githubUserConnector";
export const MCP_GITHUB_CONTEXT_ENABLED_KEY = "xhandle.mcp.githubContextEnabled";
export const MCP_GITHUB_DEFAULT_ENDPOINT = "https://api.githubcopilot.com/mcp/";
export const MCP_GITHUB_USER_CONNECTOR_ID = "github-user-configured";
export const MCP_GITHUB_READ_ONLY_ALLOWED_TOOLS = [
  "search_repositories",
  "list_commits",
  "list_pull_requests",
  "list_issues",
  "get_file_contents",
];

function safeStorage(storage) {
  if (storage) return storage;
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

export function getSelectedGitHubMcpConnectorId(storage) {
  try {
    return safeStorage(storage)?.getItem(MCP_GITHUB_CONNECTOR_ID_KEY) || "";
  } catch {
    return "";
  }
}

export function setSelectedGitHubMcpConnectorId(connectorId, storage) {
  const value = String(connectorId || "").trim();
  const target = safeStorage(storage);
  try {
    if (value) {
      target?.setItem(MCP_GITHUB_CONNECTOR_ID_KEY, value);
    } else {
      target?.removeItem(MCP_GITHUB_CONNECTOR_ID_KEY);
    }
  } catch {
    // Preference persistence is best-effort; the app can still run without it.
  }
  return value;
}

export function notifyGitHubMcpConnectorChanged(connectorId, eventTarget = window) {
  try {
    eventTarget?.dispatchEvent?.(new CustomEvent(MCP_GITHUB_CONNECTOR_CHANGED_EVENT, {
      detail: { connectorId: String(connectorId || "").trim() },
    }));
  } catch {
    // Older test/browser environments may not support CustomEvent.
  }
}

function last4(value) {
  const text = String(value || "").trim();
  return text ? text.slice(-4) : "";
}

export function getGitHubMcpContextEnabled(storage) {
  try {
    return safeStorage(storage)?.getItem(MCP_GITHUB_CONTEXT_ENABLED_KEY) === "true";
  } catch {
    return false;
  }
}

export function setGitHubMcpContextEnabled(enabled, storage) {
  const target = safeStorage(storage);
  try {
    target?.setItem(MCP_GITHUB_CONTEXT_ENABLED_KEY, enabled ? "true" : "false");
  } catch {
    // Preference persistence is best-effort.
  }
  return Boolean(enabled);
}

export function normalizeGitHubMcpUserConnector(config = {}) {
  const url = String(config.url || MCP_GITHUB_DEFAULT_ENDPOINT).trim();
  const token = String(config.token || "").replace(/^Bearer\s+/i, "").trim();
  if (!url) return null;
  return {
    id: MCP_GITHUB_USER_CONNECTOR_ID,
    name: "GitHub MCP",
    url,
    token,
    last4: last4(token || config.last4),
    allowedTools: MCP_GITHUB_READ_ONLY_ALLOWED_TOOLS,
  };
}

export function getGitHubMcpUserConnector(storage) {
  try {
    const raw = safeStorage(storage)?.getItem(MCP_GITHUB_USER_CONNECTOR_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return normalizeGitHubMcpUserConnector(parsed);
  } catch {
    return normalizeGitHubMcpUserConnector({});
  }
}

export function setGitHubMcpUserConnector(config = {}, storage) {
  const normalized = normalizeGitHubMcpUserConnector(config);
  const target = safeStorage(storage);
  try {
    if (normalized?.token) {
      target?.setItem(MCP_GITHUB_USER_CONNECTOR_KEY, JSON.stringify({
        url: normalized.url,
        token: normalized.token,
        last4: normalized.last4,
      }));
    } else {
      target?.setItem(MCP_GITHUB_USER_CONNECTOR_KEY, JSON.stringify({
        url: normalized?.url || MCP_GITHUB_DEFAULT_ENDPOINT,
        last4: "",
      }));
    }
  } catch {
    // Preference persistence is best-effort.
  }
  return getGitHubMcpUserConnector(storage);
}

export function clearGitHubMcpUserConnector(storage) {
  try {
    safeStorage(storage)?.removeItem(MCP_GITHUB_USER_CONNECTOR_KEY);
  } catch {
    // Preference persistence is best-effort.
  }
  return normalizeGitHubMcpUserConnector({});
}

export function getGitHubMcpUserConnectorMetadata(storage) {
  const connector = getGitHubMcpUserConnector(storage);
  return {
    id: connector.id,
    name: connector.name,
    url: connector.url,
    hasToken: Boolean(connector.token),
    last4: connector.last4,
    allowedTools: connector.allowedTools,
  };
}

export function getGitHubMcpUserConnectorForBackend(storage) {
  const connector = getGitHubMcpUserConnector(storage);
  if (!connector?.token) return null;
  return {
    id: connector.id,
    name: connector.name,
    url: connector.url,
    token: connector.token,
    allowedTools: connector.allowedTools,
  };
}

export function getUserExternalMcpConnectorsForBackend(storage) {
  return [getGitHubMcpUserConnectorForBackend(storage)].filter(Boolean);
}

export function normalizeGitHubMcpRepository(repo = {}) {
  const owner = String(repo.owner || "").trim();
  const name = String(repo.repo || repo.name || "").replace(/^.*\//, "").trim();
  if (!owner || !name) return null;
  const id = `${owner}/${name}`;
  return {
    id,
    owner,
    repo: name,
    label: String(repo.label || id).trim() || id,
    selected: Boolean(repo.selected),
  };
}

export function getGitHubMcpRepositories(storage) {
  try {
    const raw = safeStorage(storage)?.getItem(MCP_GITHUB_REPOSITORIES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const seen = new Set();
    return (Array.isArray(parsed) ? parsed : [])
      .map(normalizeGitHubMcpRepository)
      .filter(Boolean)
      .filter((repo) => {
        if (seen.has(repo.id)) return false;
        seen.add(repo.id);
        return true;
      });
  } catch {
    return [];
  }
}

export function setGitHubMcpRepositories(repositories = [], storage) {
  const seen = new Set();
  const normalized = (Array.isArray(repositories) ? repositories : [])
    .map(normalizeGitHubMcpRepository)
    .filter(Boolean)
    .filter((repo) => {
      if (seen.has(repo.id)) return false;
      seen.add(repo.id);
      return true;
    });
  const selectedId = normalized.find((repo) => repo.selected)?.id || normalized[0]?.id || "";
  const persisted = normalized.map((repo) => ({
    ...repo,
    selected: repo.id === selectedId,
  }));
  const target = safeStorage(storage);
  try {
    if (persisted.length) {
      target?.setItem(MCP_GITHUB_REPOSITORIES_KEY, JSON.stringify(persisted));
    } else {
      target?.removeItem(MCP_GITHUB_REPOSITORIES_KEY);
    }
  } catch {
    // Preference persistence is best-effort; the app can still run without it.
  }
  return persisted;
}

export function getSelectedGitHubMcpRepository(storage) {
  const repositories = getGitHubMcpRepositories(storage);
  return repositories.find((repo) => repo.selected) || repositories[0] || null;
}

export function upsertGitHubMcpRepository(repo = {}, storage) {
  const normalized = normalizeGitHubMcpRepository(repo);
  if (!normalized) return getGitHubMcpRepositories(storage);
  const existing = getGitHubMcpRepositories(storage);
  const selectedId = normalized.selected
    ? normalized.id
    : existing.find((candidate) => candidate.selected)?.id || normalized.id;
  const next = [
    normalized,
    ...existing.filter((candidate) => candidate.id !== normalized.id),
  ].map((candidate) => ({
    ...candidate,
    selected: candidate.id === selectedId,
  }));
  return setGitHubMcpRepositories(next, storage);
}

export function selectGitHubMcpRepository(repoId, storage) {
  const id = String(repoId || "").trim();
  const repositories = getGitHubMcpRepositories(storage);
  return setGitHubMcpRepositories(
    repositories.map((repo, index) => ({
      ...repo,
      selected: id ? repo.id === id : index === 0,
    })),
    storage
  );
}

export function removeGitHubMcpRepository(repoId, storage) {
  const id = String(repoId || "").trim();
  const remaining = getGitHubMcpRepositories(storage).filter((repo) => repo.id !== id);
  if (remaining.length && !remaining.some((repo) => repo.selected)) {
    remaining[0] = { ...remaining[0], selected: true };
  }
  return setGitHubMcpRepositories(remaining, storage);
}

export function notifyGitHubMcpRepositoryChanged(repository, eventTarget = window) {
  try {
    eventTarget?.dispatchEvent?.(new CustomEvent(MCP_GITHUB_REPOSITORY_CHANGED_EVENT, {
      detail: { repository: repository || null },
    }));
  } catch {
    // Older test/browser environments may not support CustomEvent.
  }
}

export function isGitHubMcpConnector(connector = {}) {
  const haystack = [
    connector.id,
    connector.name,
    connector.description,
    ...(Array.isArray(connector.allowedTools) ? connector.allowedTools : []),
  ].join(" ").toLowerCase();
  return haystack.includes("github")
    || haystack.includes("github_repo_summary")
    || haystack.includes("github_recent_activity");
}

export function listGitHubMcpConnectorCandidates(connectors = []) {
  return (Array.isArray(connectors) ? connectors : []).filter(isGitHubMcpConnector);
}
